import { memo } from "react"
import {
	IconArrowRotateClockwiseOutline18,
	IconBoltOutline18,
	IconCheckOutline18,
	IconCirclePercentageOutline18,
	IconClockOutline18,
	IconEnvelopeCheckOutline18,
	IconGaugeOutline18,
	IconUserOutline18,
} from "nucleo-ui-outline-18"

import type { ReportingQueryProps } from "../reporting-query"
import type { ReportingTone } from "@/components/reporting/charts"
import {
	formatDurationHours,
	ReportingAreaChart,
	ReportingHeatmap,
	ReportingKpiCard,
} from "@/components/reporting/charts"
import { api } from "@/lib/api"

export const ResponsivenessTab = memo((props: ReportingQueryProps) => {
	const { startDate, endDate, ...filters } = props
	const query = api.workspace.getReportingResponsiveness.useQuery(
		{ startDate, endDate, ...filters },
		{ cacheTime: Infinity, staleTime: 60_000 },
	)
	const data = query.data
	const loading = query.isLoading

	const topKpis: Array<{
		label: string
		value: string
		tone: ReportingTone
		Icon: typeof IconClockOutline18
	}> = [
		{
			label: "TTFR avg",
			value: data?.ttfr.avg != null ? formatDurationHours(data.ttfr.avg) : "-",
			tone: "categorical-0",
			Icon: IconClockOutline18,
		},
		{
			label: "TTFR median",
			value:
				data?.ttfr.median != null ? formatDurationHours(data.ttfr.median) : "-",
			tone: "categorical-5",
			Icon: IconGaugeOutline18,
		},
		{
			label: "TTFR p90",
			value: data?.ttfr.p90 != null ? formatDurationHours(data.ttfr.p90) : "-",
			tone: "categorical-2",
			Icon: IconBoltOutline18,
		},
		{
			label: "Close median",
			value:
				data?.timeToClose.median != null
					? formatDurationHours(data.timeToClose.median)
					: "-",
			tone: "categorical-1",
			Icon: IconCheckOutline18,
		},
	]

	const bottomKpis: Array<{
		label: string
		value: string | number
		tone: ReportingTone
		Icon: typeof IconClockOutline18
	}> = [
		{
			label: "Time to assign",
			value:
				data?.timeToAssign.median != null
					? formatDurationHours(data.timeToAssign.median)
					: "-",
			tone: "categorical-3",
			Icon: IconUserOutline18,
		},
		{
			label: "First contact close %",
			value: data?.firstContactCloseRate ?? "-",
			tone: "categorical-4",
			Icon: IconCirclePercentageOutline18,
		},
		{
			label: "Avg replies to close",
			value:
				data?.avgRepliesToClose != null
					? Math.round(data.avgRepliesToClose * 10) / 10
					: "-",
			tone: "categorical-7",
			Icon: IconEnvelopeCheckOutline18,
		},
		{
			label: "Reassigns",
			value: data?.reassignCount ?? "-",
			tone: "categorical-6",
			Icon: IconArrowRotateClockwiseOutline18,
		},
	]

	return (
		<div className="space-y-4 p-4 sm:p-6">
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				{topKpis.map((kpi) => (
					<ReportingKpiCard
						key={kpi.label}
						label={kpi.label}
						value={kpi.value}
						isLoading={loading}
						Icon={kpi.Icon}
						tone={kpi.tone}
					/>
				))}
			</div>

			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				{bottomKpis.map((kpi) => (
					<ReportingKpiCard
						key={kpi.label}
						label={kpi.label}
						value={kpi.value}
						isLoading={loading}
						Icon={kpi.Icon}
						tone={kpi.tone}
					/>
				))}
			</div>

			<ReportingAreaChart
				title="Median first response over time"
				data={(data?.ttfr.series ?? []).map((p) => ({
					date: p.date,
					value: p.value,
				}))}
				series={[
					{
						key: "value",
						label: "Median TTFR (h)",
						tone: "categorical-0",
					},
				]}
				isLoading={loading}
				valueFormatter={formatDurationHours}
			/>

			<ReportingHeatmap
				title="Hourly distribution of first responses"
				description="Stronger green means more first responses in that hour."
				cells={data?.responseHeatmap ?? []}
				isLoading={loading}
				scale="activity"
			/>
		</div>
	)
})
